import api from './api';

/**
 * Subscriptions Service
 * Handles subscription plans and user subscriptions API calls
 */
const subscriptionsService = {
    // ==================== Plans ====================
    
    /**
     * Get all subscription plans
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getAllPlans: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/subscriptions/plans${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get plan by ID
     * @param {string} id - Plan ID
     * @returns {Promise<Object>}
     */
    getPlanById: async (id) => {
        return api.get(`/admin/subscriptions/plans/${id}`);
    },

    /**
     * Create new plan
     * @param {Object} data - Plan data
     * @returns {Promise<Object>}
     */
    createPlan: async (data) => {
        return api.post('/admin/subscriptions/plans', data);
    },

    /**
     * Update plan
     * @param {string} id - Plan ID
     * @param {Object} data - Updated plan data
     * @returns {Promise<Object>}
     */
    updatePlan: async (id, data) => {
        return api.put(`/admin/subscriptions/plans/${id}`, data);
    },

    /**
     * Delete plan
     * @param {string} id - Plan ID
     * @param {string} queryString - Optional query string (e.g., '?force=true')
     * @returns {Promise<Object>}
     */
    deletePlan: async (id, queryString = '') => {
        return api.delete(`/admin/subscriptions/plans/${id}${queryString}`);
    },

    /**
     * Set plan as default
     * @param {string} id - Plan ID
     * @returns {Promise<Object>}
     */
    setDefaultPlan: async (id) => {
        return api.patch(`/admin/subscriptions/plans/${id}/set-default`);
    },

    /**
     * Assign plan to all users
     * @param {string} id - Plan ID
     * @param {Object} data - { overwriteExisting: boolean }
     * @returns {Promise<Object>}
     */
    assignPlanToAll: async (id, data = {}) => {
        return api.post(`/admin/subscriptions/plans/${id}/assign-to-all`, data);
    },

    // ==================== User Subscriptions ====================

    /**
     * Get all user subscriptions
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getAllSubscriptions: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/subscriptions${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Activate user subscription
     * @param {string} id - Subscription ID
     * @param {Object} data - { duration, reason }
     * @returns {Promise<Object>}
     */
    activateSubscription: async (id, data = {}) => {
        return api.patch(`/admin/subscriptions/${id}/activate`, data);
    },

    /**
     * Cancel user subscription
     * @param {string} id - Subscription ID
     * @param {Object} data - { reason }
     * @returns {Promise<Object>}
     */
    cancelSubscription: async (id, data = {}) => {
        return api.patch(`/admin/subscriptions/${id}/cancel`, data);
    },

    /**
     * Suspend user subscription
     * @param {string} id - Subscription ID
     * @param {Object} data - { reason }
     * @returns {Promise<Object>}
     */
    suspendSubscription: async (id, data = {}) => {
        return api.patch(`/admin/subscriptions/${id}/suspend`, data);
    },

    /**
     * Change user subscription plan (Upgrade/Downgrade)
     * @param {string} id - Subscription ID
     * @param {Object} data - { planId, duration, reason }
     * @returns {Promise<Object>}
     */
    changePlan: async (id, data) => {
        return api.patch(`/admin/subscriptions/${id}/change-plan`, data);
    },

    /**
     * Assign plan to user
     * @param {Object} data - { userId, planId, duration, notes, subscriptionType }
     * @returns {Promise<Object>}
     */
    assignPlanToUser: async (data) => {
        return api.post('/admin/subscriptions/assign', data);
    },

    /**
     * Get subscription logs
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getLogs: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/subscriptions/logs${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get subscription logs for specific subscription
     * @param {string} id - Subscription ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getSubscriptionLogs: async (id, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/subscriptions/${id}/logs${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Delete subscription permanently
     * @param {string} id - Subscription ID
     * @returns {Promise<Object>}
     */
    deleteSubscription: async (id) => {
        return api.delete(`/admin/subscriptions/${id}`);
    },

    /**
     * Get subscription statistics
     * @returns {Promise<Object>}
     */
    getStats: async () => {
        return api.get('/admin/subscriptions/stats');
    }
};

export default subscriptionsService;
